import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Diamond, Dribbble, Shield, CircleDot, Goal, Sparkles, Volleyball, Footprints, Shirt } from 'lucide-react';
import { Container } from '../Container';
import { motion } from 'framer-motion';
const SPORTS = [{
  name: 'Baseball',
  desc: 'Full button, two-button, v-neck, and pants.',
  path: '/sports/baseball',
  icon: Diamond,
  image: 'https://cdn.magicpatterns.com/uploads/gPpbwvNw14opGcecGn7ueW/gcu-sport-baseball.png',
  alt: 'Custom baseball uniforms and gear'
}, {
  name: 'Basketball',
  desc: 'Reversible jerseys, shorts, and shooter shirts.',
  path: '/sports/basketball',
  icon: Dribbble,
  image: 'https://cdn.magicpatterns.com/uploads/mBdkKAcRAk3GTpwJ9ZEXxH/gcu-sport-basketball.png',
  alt: 'Custom basketball uniforms and gear'
}, {
  name: 'Football',
  desc: 'Durable game jerseys, practice gear, and pants.',
  path: '/sports/football',
  icon: Shield,
  image: 'https://cdn.magicpatterns.com/uploads/binVcLko4YrzPR7j93DSyy/gcu-sport-football.png',
  alt: 'Custom football uniforms and gear'
}, {
  name: 'Softball',
  desc: 'Jerseys, pants, and custom team apparel.',
  path: '/sports/softball',
  icon: CircleDot,
  image: 'https://cdn.magicpatterns.com/uploads/gUPTojp7m1z9kbv3ycPEHR/gcu-sport-softball.png',
  alt: 'Custom softball uniforms and gear'
}, {
  name: 'Soccer',
  desc: 'Full kits, goalie gear, and warm-ups.',
  path: '/sports/soccer',
  icon: Goal,
  image: 'https://cdn.magicpatterns.com/uploads/ebKGzySDHMm7Xt9bZKSZ16/gcu-sport-soccer.png',
  alt: 'Custom soccer uniforms and gear'
}, {
  name: 'Cheer',
  desc: 'Custom uniforms, warm-ups, and bags.',
  path: '/sports/cheer',
  icon: Sparkles,
  image: 'https://cdn.magicpatterns.com/uploads/94rVc1mYvaK6Eiicii3B1P/gcu-sport-cheer.png',
  alt: 'Custom cheer uniforms and gear'
}, {
  name: 'Volleyball',
  desc: 'Long sleeve, short sleeve, and sleeveless options.',
  path: '/sports/volleyball',
  icon: Volleyball,
  image: 'https://cdn.magicpatterns.com/uploads/6qPzGqmnAEGczD4X7uyyA3/gcu-sport-volleyball.png',
  alt: 'Custom volleyball uniforms and gear'
}, {
  name: 'Track & Field',
  desc: 'Singlets, compression gear, and warm-ups.',
  path: '/sports/track-field',
  icon: Footprints,
  image: 'https://cdn.magicpatterns.com/uploads/2EbTSiGXBGibw6yQkDGXSL/gcu-sport-track-field.png',
  alt: 'Custom track and field uniforms and gear'
}, {
  name: 'Groupwear',
  desc: 'Corporate, staff, and organizational apparel.',
  path: '/sports/groupwear',
  icon: Shirt,
  image: 'https://cdn.magicpatterns.com/uploads/pGcTPLJcXP8MnYEqpemrPy/gcu-sport-groupwear.png',
  alt: 'Custom groupwear apparel and team gear'
}];
export function PickYourSport() {
  return <section className="py-20 bg-white" data-id="element-220">
      <Container data-id="element-221">
        <div className="text-center mb-12" data-id="element-222">
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase mb-4 text-gray-900" data-id="element-223">
            Pick Your Sport
          </h2>
          <div className="w-24 h-1.5 bg-pts-red mx-auto mb-6" data-id="element-224"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-225">
            Select your sport to view custom uniform options, team packages, and
            design inspiration.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.1
      }} transition={{
        duration: 0.4
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-226">
          {SPORTS.map(sport => <Link key={sport.name} to={sport.path} className="group block bg-gray-50 border border-gray-200 rounded-lg hover:shadow-lg hover:-translate-y-1 hover:border-pts-red transition-all duration-300 relative overflow-hidden flex flex-col h-full" data-id="element-227">
              <div className="absolute top-0 left-0 w-full h-1 bg-pts-red transform -translate-y-full group-hover:translate-y-0 transition-transform duration-300 z-10" data-id="element-228"></div>

              {/* Sport Thumbnail */}
              <div className="w-full aspect-[4/3] bg-gray-100 relative overflow-hidden" data-id="element-229">
                <img src={sport.image} alt={sport.alt} loading="lazy" className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105" data-id="element-230" />
              </div>

              <div className="p-6 flex flex-col flex-grow" data-id="element-231">
                <div className="flex items-start justify-between mb-4" data-id="element-232">
                  <h3 className="font-oswald text-2xl font-bold text-gray-900 uppercase tracking-wide" data-id="element-233">
                    {sport.name}
                  </h3>
                  <div className="bg-gray-100 group-hover:bg-pts-red group-hover:text-white p-2 rounded-full transition-colors duration-300 text-gray-700" data-id="element-234">
                    <sport.icon className="h-5 w-5" data-id="element-235" />
                  </div>
                </div>

                <p className="text-gray-600 mb-6 flex-grow" data-id="element-236">{sport.desc}</p>

                <div className="flex items-center text-pts-red font-bold text-sm uppercase tracking-wider group-hover:text-pts-red-dark mt-auto" data-id="element-237">
                  View Options{' '}
                  <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-238" />
                </div>
              </div>
            </Link>)}
        </motion.div>
      </Container>
    </section>;
}